import React from 'react';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import {
  getFirstDayOfTheWeek,
  getLastDayOfTheWeek,
  formatDate
} from '@shared/services/dateService';
import AvailableAppraisersInfoBox from '../WeeklyCalendar/AvailableAppraisersInfoBox';
import WeeklyDatePicker from '../WeeklyCalendar/WeeklyDatePicker';
import ApolloError from '@shared/components/ApolloError';
import GET_ADMIN_CALENDAR from '../../queries/getAdminCalendarQuery';
import AddEventLinkButton from '../shared/components/AddEventLinkButton';
import WeeklyCalendar from '../WeeklyCalendar/WeeklyCalendar';
import CalendarEventMapper from '../../mappers/CalendarEventMapper';
import ViewSwitch from '../shared/components/ViewSwitch';
import { useReactiveVar } from '@apollo/client';
import { userPrivileges } from '@apollo-internal/cache';
import PrivilegeNames from '../../../../constants/privilegeNames';
import { useSetSelectedDate } from '../shared/hooks/useSetSelectedDate';

const CHECKED_LABEL = 'Admin';

const AdminCalendar = () => {
  const privileges = useReactiveVar(userPrivileges);

  const { selectedDate, setSelectedDate } = useSetSelectedDate();

  const {
    data: calendarData,
    loading: calendarDataLoading,
    error: calendarDataError
  } = useCacheNetworkQuery(GET_ADMIN_CALENDAR, {
    variables: {
      start: formatDate(getFirstDayOfTheWeek(selectedDate)),
      end: formatDate(getLastDayOfTheWeek(selectedDate))
    },
    skip: !Boolean(selectedDate)
  });

  const appraiserEvents = calendarData
    ? CalendarEventMapper.toCalendarEventData(calendarData.adminAppraiserEvents)
    : [];

  if (calendarDataError) {
    return <ApolloError />;
  }

  return (
    <div className='calendar-view'>
      <div className='add-event-section'>
        <AddEventLinkButton />
      </div>
      <div className='calendar-header'>
        <WeeklyDatePicker
          loading={calendarDataLoading}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />

        {privileges.includes(PrivilegeNames.Calendar_ATMToggle) && (
          <ViewSwitch activeLabel={CHECKED_LABEL} />
        )}

        <div className='legends'>
          <span className='draft'>Draft</span>
          <span className='atl-assigned'>ATL Assigned</span>
          <span className='open-for-bid'>Open for Bid</span>
          <span className='team-assigned'>Team Assigned</span>
          <span className='ready-to-deliver'>Ready to Deliver</span>
        </div>
      </div>

      <div className='calendar-container'>
        <div className='appraisers-info-box'>
          <SkeletonWrapper
            show={calendarDataLoading}
            inheritChildHeight
            renderComponent={() => (
              <AvailableAppraisersInfoBox
                availableAppraisers={calendarData?.availableAppraisers || {}}
              />
            )}
          />
        </div>
        <div className='calendar-body'>
          <WeeklyCalendar
            selectedDate={selectedDate}
            events={appraiserEvents}
            hideBidWarnings={true}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;
